
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Checkbox,
  Spinner,
  Grid,
} from '@chakra-ui/react'
import { createNavigationSchema, updateNavigationSchema, type CreateNavigationFormData, type UpdateNavigationFormData } from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'

interface Navigation {
  id: number
  name: string
  slug: string
  parent_id: number | null
  children?: Navigation[]
}

interface NavigationFormProps {
  navigationId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function NavigationForm({ navigationId, onSuccess, onCancel }: NavigationFormProps) {
  const colors = useThemeColors()
  const [navigations, setNavigations] = useState<Navigation[]>([])
  const [isLoadingNavigations, setIsLoadingNavigations] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(!!navigationId)
  const isEditMode = !!navigationId

  const { showError, showSuccess } = useToastNotification()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateNavigationFormData | UpdateNavigationFormData>({
    resolver: zodResolver(isEditMode ? updateNavigationSchema : createNavigationSchema),
    defaultValues: {
      name: '',
      slug: '',
      icon: '',
      parent_id: null,
      active: true,
      show_in_menu: true,
      application: 'auth', // Default to 'auth' for System Settings > Navigation
    },
  })

  const active = watch('active')
  const showInMenu = watch('show_in_menu')

  // Fetch navigations for parent selection
  useEffect(() => {
    const fetchNavigations = async () => {
      try {
        const response = await apiClient.get('/options/routes?application=auth')
        const navData = response.data?.data || response.data || []
        setNavigations(Array.isArray(navData) ? navData : [])
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoadingNavigations(false)
      }
    }

    fetchNavigations()
  }, [showError])

  // Fetch navigation data if editing
  useEffect(() => {
    if (navigationId) {
      const fetchNavigation = async () => {
        try {
          setIsLoading(true)
          const response = await apiClient.get(`/system-settings/navigation/${navigationId}`)
          const data = response.data?.data || response.data
          
          if (data) {
            reset({
              name: data.name || '',
              slug: data.slug || '',
              icon: data.icon || '',
              parent_id: data.parent_id || null,
              active: data.active !== undefined ? data.active : true,
              show_in_menu: data.show_in_menu !== undefined ? data.show_in_menu : true,
              application: data.application || 'auth',
            })
          }
        } catch (error: any) {
          showError(error)
        } finally {
          setIsLoading(false)
        }
      }

      fetchNavigation()
    }
  }, [navigationId, reset, showError])

  // Flatten navigation tree for select options
  const flattenNavigations = (navs: Navigation[], level = 0): Array<Navigation & { displayName: string }> => {
    const result: Array<Navigation & { displayName: string }> = []
    navs.forEach((nav) => {
      result.push({
        ...nav,
        displayName: '  '.repeat(level) + nav.name,
      })
      if (nav.children && nav.children.length > 0) {
        result.push(...flattenNavigations(nav.children, level + 1))
      }
    })
    return result
  }

  const navigationOptions = flattenNavigations(navigations)

  const onSubmit = async (data: CreateNavigationFormData | UpdateNavigationFormData) => {
    setIsSubmitting(true)
    try {
      // Prepare payload - convert parent_id 0 to null, ensure proper types
      const payload = {
        ...data,
        parent_id: data.parent_id === 0 || data.parent_id === null ? null : data.parent_id,
        active: data.active ?? true,
        show_in_menu: data.show_in_menu ?? true,
        application: data.application || null,
      }
      
      if (isEditMode && navigationId) {
        await apiClient.put(`/system-settings/navigation/${navigationId}`, payload)
        showSuccess('Success', 'Navigation updated successfully')
      } else {
        await apiClient.post('/system-settings/navigation', payload)
        showSuccess('Success', 'Navigation created successfully')
      }
      onSuccess?.()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text mt={4}>Loading navigation...</Text>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="Enter navigation name" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Slug <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.slug}>
            <Input {...register('slug')} placeholder="Enter navigation slug (e.g., system-settings/navigation)" />
            <FormErrorMessage>{errors.slug?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Icon</FormLabel>
          <FormControl isInvalid={!!errors.icon}>
            <Input {...register('icon')} placeholder="Enter icon name (e.g., menu, settings)" />
            <FormErrorMessage>{errors.icon?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Parent Navigation</FormLabel>
          <FormControl isInvalid={!!errors.parent_id}>
            <Select
              {...register('parent_id', {
                setValueAs: (value) => (value === '' || value === 'null' ? null : Number(value)),
              })}
              placeholder="Select parent navigation (optional)"
            >
              <option value="">None (Root Level)</option>
              {navigationOptions.map((nav) => (
                <option key={nav.id} value={nav.id} disabled={isEditMode && nav.id === navigationId}>
                  {nav.displayName}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.parent_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Active</FormLabel>
          <FormControl>
            <HStack>
              <Checkbox {...register('active')} isChecked={active}>
                Active
              </Checkbox>
              <Text fontSize="sm" color={colors.text}>
                Navigation item is active and visible
              </Text>
            </HStack>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Show in Menu</FormLabel>
          <FormControl>
            <HStack>
              <Checkbox {...register('show_in_menu')} isChecked={showInMenu}>
                Show in Menu
              </Checkbox>
              <Text fontSize="sm" color={colors.text}>
                Display this item in the navigation menu
              </Text>
            </HStack>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Application</FormLabel>
          <FormControl isInvalid={!!errors.application}>
            <Select
              {...register('application', {
                setValueAs: (value) => (value === '' || value === 'null' ? null : value),
              })}
              placeholder="Select application (optional)"
            >
              <option value="">None (Shared)</option>
              <option value="auth">Auth</option>
              <option value="sima">SIMA</option>
              <option value="support">Support</option>
            </Select>
            <FormErrorMessage>{errors.application?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Updating...' : 'Creating...'}>
            {isEditMode ? 'Update Navigation' : 'Create Navigation'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
